<template>
  <div class="tc-view">
    <div v-if="store.loading && !store.overview" class="tc-state"><span class="mdi mdi-loading tc-state__spinner"></span></div>

    <div v-else-if="store.error === 'PORTAL_DISABLED'" class="tc-state tc-state--message">
      <span class="mdi mdi-lock-outline tc-state__icon"></span>
      <p>El portal de inquilinos no está disponible por el momento. Contacta a la administración del parque.</p>
    </div>

    <div v-else-if="store.error" class="tc-state tc-state--message">
      <span class="mdi mdi-alert-circle-outline tc-state__icon"></span>
      <p>No se pudo cargar tu información.</p>
      <button class="tc-btn" @click="reload">Reintentar</button>
    </div>

    <div v-else-if="store.overview && !store.overview.contracts.length" class="tc-state tc-state--message">
      <span class="mdi mdi-file-document-outline tc-state__icon"></span>
      <p>Tu cuenta todavía no tiene un contrato con acceso al portal. Contacta a la administración del parque.</p>
    </div>

    <div v-else-if="store.overview" class="tc-scroll">
      <div class="tc-column">
        <div v-if="store.overview.park.welcomeMessage" class="tc-welcome">{{ store.overview.park.welcomeMessage }}</div>

        <TenantContractSelect
          :contracts="store.overview.contracts"
          :model-value="store.selectedContractId"
          @update:model-value="onSelect"
        />

        <div v-if="detailLoading" class="tc-state"><span class="mdi mdi-loading tc-state__spinner"></span></div>
        <div v-else-if="detailError" class="tc-state tc-state--message">
          <p>No se pudo cargar el contrato.</p>
          <button class="tc-btn" @click="loadDetail">Reintentar</button>
        </div>

        <template v-else-if="detail">
          <!-- Encabezado -->
          <section class="tc-header">
            <div class="tc-header__grid">
              <div class="tc-header__cell">
                <span class="tc-header__label">Nave</span>
                <span class="tc-header__mono">{{ detail.plot.name }}</span>
              </div>
              <div class="tc-header__cell">
                <span class="tc-header__label">Superficie</span>
                <span class="tc-header__mono">{{ areaText }}</span>
              </div>
              <div class="tc-header__cell">
                <span class="tc-header__label">Inicio</span>
                <span class="tc-header__date">{{ formatDate(detail.startDate) }}</span>
              </div>
              <div class="tc-header__cell">
                <span class="tc-header__label">Vencimiento</span>
                <span class="tc-header__date" :style="{ color: expiryColor }">{{ formatDate(detail.endDate) }}</span>
              </div>
            </div>
            <div class="tc-progress">
              <div class="tc-progress__track"><div class="tc-progress__fill" :style="{ width: progress + '%' }"></div></div>
              <span class="tc-progress__pct">{{ Math.round(progress) }}%</span>
            </div>
          </section>

          <!-- Condiciones -->
          <section>
            <h3 class="tc-label">Condiciones del contrato</h3>
            <div class="tc-table">
              <div v-for="row in conditionRows" :key="row.label" class="tc-table__row">
                <span class="tc-table__label">{{ row.label }}</span>
                <span class="tc-table__value" :class="{ 'tc-table__value--mono': row.mono }">{{ row.value }}</span>
              </div>
            </div>
          </section>

          <!-- Documentos -->
          <section>
            <h3 class="tc-label">Documentos</h3>
            <div v-if="!detail.documents.length" class="tc-empty">Aún no hay documentos disponibles.</div>
            <div v-else class="tc-docs">
              <div v-for="doc in detail.documents" :key="doc.id" class="tc-doc">
                <span class="mdi mdi-file-pdf-box tc-doc__icon"></span>
                <span class="tc-doc__name">{{ doc.fileName }}</span>
                <span class="tc-doc__date">{{ formatDate(doc.uploadedAt) }}</span>
                <a
                  v-if="doc.fileUrl"
                  class="tc-doc__download"
                  :href="`${UPLOADS_BASE}/${doc.fileUrl}`"
                  target="_blank"
                  rel="noopener"
                  :aria-label="`Descargar ${doc.fileName}`"
                ><span class="mdi mdi-download"></span></a>
                <span v-else class="tc-doc__download tc-doc__download--off"><span class="mdi mdi-download"></span></span>
              </div>
            </div>
          </section>

          <!-- Contactos -->
          <section v-if="detail.parkContacts.length">
            <h3 class="tc-label">Contactos del parque</h3>
            <div class="tc-contacts">
              <div v-for="c in detail.parkContacts" :key="c.id" class="tc-contact">
                <div class="tc-contact__info">
                  <span class="tc-contact__name">{{ c.name }}</span>
                  <span v-if="c.role" class="tc-contact__role">{{ c.role }}</span>
                </div>
                <button
                  class="tc-contact__action"
                  :disabled="!c.phone"
                  :title="c.phone ? `Copiar ${c.phone}` : 'Sin teléfono'"
                  @click="copy(c.phone, 'Teléfono copiado')"
                ><span class="mdi mdi-phone-outline"></span></button>
                <button
                  class="tc-contact__action"
                  :disabled="!c.email"
                  :title="c.email ? `Copiar ${c.email}` : 'Sin correo'"
                  @click="copy(c.email, 'Correo copiado')"
                ><span class="mdi mdi-email-outline"></span></button>
              </div>
            </div>
          </section>
        </template>
      </div>
    </div>

    <transition name="tc-toast">
      <div v-if="toast" class="tc-toast">{{ toast }}</div>
    </transition>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { getPortalContract } from '@/services/tenantPortal'
import { useTenantPortalStore } from '@/services/tenantPortalStore'
import TenantContractSelect from '@/components/TenantPortal/TenantContractSelect.vue'
import { UPLOADS_BASE } from '@/components/TenantPortal/tenantNav'

const { state: store, loadOverview, selectContract } = useTenantPortalStore()

const detail = ref(null)
const detailLoading = ref(false)
const detailError = ref(false)
const toast = ref('')

async function reload() {
  try {
    await loadOverview(true)
  } catch {
    /* el error queda en store.error */
  }
}

async function loadDetail() {
  if (!store.selectedContractId) {
    detail.value = null
    return
  }
  detailLoading.value = true
  detailError.value = false
  try {
    detail.value = await getPortalContract(store.selectedContractId)
  } catch {
    detail.value = null
    detailError.value = true
  } finally {
    detailLoading.value = false
  }
}

function onSelect(id) {
  selectContract(id)
}

onMounted(async () => {
  await reload()
  await loadDetail()
})
watch(() => store.selectedContractId, (next, prev) => {
  if (prev !== null && next !== prev) loadDetail()
})

// --- Presentación ---
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
}
function formatMoney(amount, currency) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency || 'MXN' }).format(amount || 0)
}

const areaText = computed(() => {
  const area = detail.value?.plot.areaM2
  return area ? `${new Intl.NumberFormat('es-MX').format(area)} m²` : '—'
})
const progress = computed(() => Math.min(100, Math.max(0, detail.value?.progressPercent ?? 0)))
// Verde con más de 90 días, ámbar entre 30 y 90, rojo con menos de 30 (o ya vencido).
const expiryColor = computed(() => {
  const days = detail.value?.daysRemaining ?? 0
  if (days > 90) return '#10b981'
  if (days >= 30) return '#f59e0b'
  return '#ef4444'
})

const CONTRACT_TYPES = { FIXED: 'Renta fija', VARIABLE: 'Renta variable', MIXED: 'Renta mixta' }
const conditionRows = computed(() => {
  const c = detail.value?.conditions
  if (!c) return []
  const rows = [
    { label: 'Renta mensual', value: formatMoney(c.monthlyRent, c.currency), mono: true },
    { label: 'Moneda', value: c.currency },
    { label: 'Día de vencimiento de renta', value: c.paymentDay ? `Día ${c.paymentDay} de cada mes` : '—' },
    { label: 'Esquema de renta', value: CONTRACT_TYPES[c.contractType] || '—' },
  ]
  c.nextEscalations.forEach((e, i) => {
    const pct = e.percentIncrease === null ? '' : ` (${e.percentIncrease >= 0 ? '+' : ''}${e.percentIncrease.toFixed(1)}%)`
    rows.push({
      label: i === 0 ? 'Próxima escalación' : 'Escalación posterior',
      value: `${formatMoney(e.newRent, c.currency)} desde el ${formatDate(e.date)}${pct}`,
      mono: false,
    })
  })
  if (detail.value.plot.location) rows.push({ label: 'Ubicación en el parque', value: detail.value.plot.location })
  return rows
})

let toastTimer = null
async function copy(text, message) {
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
    toast.value = message
  } catch {
    toast.value = text
  }
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => (toast.value = ''), 1800)
}
</script>

<style scoped>
.tc-view { position: relative; flex: 1; min-height: 0; display: flex; flex-direction: column; background: #fff; }
.tc-scroll { flex: 1; overflow-y: auto; padding: 24px 16px 32px; }
.tc-column { max-width: 720px; margin: 0 auto; display: flex; flex-direction: column; gap: 24px; }

.tc-state { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 40px 16px; color: #94a3b8; }
.tc-state--message { text-align: center; font: 400 13px Inter, sans-serif; }
.tc-state__icon { font-size: 32px; }
.tc-state__spinner { font-size: 28px; animation: tc-spin 1s linear infinite; }
@keyframes tc-spin { to { transform: rotate(360deg); } }
.tc-btn { height: 32px; padding: 0 14px; border: none; border-radius: 6px; background: #3b82f6; color: #fff; font: 500 13px Inter, sans-serif; cursor: pointer; }

.tc-welcome { padding: 12px 14px; border-radius: 8px; background: #eff6ff; border: 1px solid #bfdbfe; color: #1e3a5f; font: 400 13px/1.5 Inter, sans-serif; }

.tc-label { margin: 0 0 8px; font: 500 11px Inter, sans-serif; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }

.tc-header { background: #f0f4f8; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 16px; }
.tc-header__grid { display: grid; grid-template-columns: repeat(4, 1fr); }
.tc-header__cell { display: flex; flex-direction: column; gap: 4px; padding: 0 14px; border-left: 1px solid #e2e8f0; min-width: 0; }
.tc-header__cell:first-child { border-left: none; padding-left: 0; }
.tc-header__label { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.tc-header__mono { font: 700 18px var(--font-mono); color: #1e3a5f; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tc-header__date { font: 500 14px Inter, sans-serif; color: #1e3a5f; padding-top: 3px; }
.tc-progress { display: flex; align-items: center; gap: 10px; margin-top: 14px; }
.tc-progress__track { flex: 1; height: 6px; border-radius: 3px; background: #e2e8f0; overflow: hidden; }
.tc-progress__fill { height: 100%; background: #3b82f6; border-radius: 3px; }
.tc-progress__pct { font: 400 11px Inter, sans-serif; color: #94a3b8; min-width: 30px; text-align: right; }

.tc-table { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.tc-table__row { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; padding: 10px 14px; border-top: 1px solid #f1f5f9; }
.tc-table__row:first-child { border-top: none; }
.tc-table__label { font: 400 12px Inter, sans-serif; color: #64748b; }
.tc-table__value { font: 500 13px Inter, sans-serif; color: #1e3a5f; text-align: right; }
.tc-table__value--mono { font: 400 13px var(--font-mono); }

.tc-empty { font: 400 12px Inter, sans-serif; color: #94a3b8; text-align: center; padding: 16px; }
.tc-docs { border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.tc-doc { display: flex; align-items: center; gap: 10px; height: 44px; padding: 0 12px; border-top: 1px solid #f1f5f9; }
.tc-doc:first-child { border-top: none; }
.tc-doc__icon { font-size: 16px; color: #ef4444; flex-shrink: 0; }
.tc-doc__name { flex: 1; min-width: 0; font: 400 13px Inter, sans-serif; color: #1e3a5f; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tc-doc__date { font: 400 11px Inter, sans-serif; color: #94a3b8; flex-shrink: 0; }
.tc-doc__download { width: 28px; height: 28px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; border-radius: 6px; color: #64748b; text-decoration: none; font-size: 16px; }
.tc-doc__download:hover { background: #f1f5f9; color: #1e3a5f; }
.tc-doc__download--off { opacity: 0.3; cursor: not-allowed; }

.tc-contacts { display: flex; flex-direction: column; gap: 8px; }
.tc-contact { display: flex; align-items: center; gap: 6px; height: 64px; padding: 0 12px; border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; }
.tc-contact__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.tc-contact__name { font: 500 13px Inter, sans-serif; color: #1e3a5f; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tc-contact__role { font: 400 11px Inter, sans-serif; color: #94a3b8; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tc-contact__action { width: 32px; height: 32px; border: none; border-radius: 6px; background: none; color: #64748b; font-size: 18px; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.tc-contact__action:hover:not(:disabled) { background: #f1f5f9; color: #3b82f6; }
.tc-contact__action:disabled { opacity: 0.3; cursor: not-allowed; }

.tc-toast { position: absolute; left: 50%; bottom: 24px; transform: translateX(-50%); background: #1e3a5f; color: #fff; padding: 8px 14px; border-radius: 6px; font: 500 12px Inter, sans-serif; z-index: 20; }
.tc-toast-enter-active, .tc-toast-leave-active { transition: opacity 0.2s; }
.tc-toast-enter-from, .tc-toast-leave-to { opacity: 0; }

@media (max-width: 768px) {
  .tc-scroll { padding: 16px 12px 24px; }
  .tc-header__grid { grid-template-columns: 1fr 1fr; row-gap: 14px; }
  .tc-header__cell:nth-child(3) { border-left: none; padding-left: 0; }
  .tc-table__row { flex-direction: column; align-items: flex-start; gap: 2px; }
  .tc-table__value { text-align: left; }
  .tc-toast { bottom: 76px; }
}
</style>